import { FC, useEffect, useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { useTranslation } from 'react-i18next'

const DOTS_COUNT  = 10
const MAX_ATTEMPTS = 3

export type SsoScreenStatus = 'connecting' | 'retrying' | 'error' | 'success'

interface SsoConnectingScreenProps {
  status:   SsoScreenStatus
  attempt:  number
  onRetry:  () => void
  onContactSupport: () => void
  logoUrl?: string
}

// Cycles the center of a 3-dot group forward every 300ms
const useActiveDot = (running: boolean) => {
  const [active, setActive] = useState(1)
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => setActive(prev => (prev + 1) % DOTS_COUNT), 300)
    return () => clearInterval(id)
  }, [running])
  return active
}

const SsoConnectingScreen: FC<SsoConnectingScreenProps> = ({ status, attempt, onRetry, onContactSupport, logoUrl }) => {
  const { t } = useTranslation()
  const activeDot = useActiveDot(status === 'connecting' || status === 'retrying')
  const isRetrying = status === 'retrying'
  const isError    = status === 'error'
  const isSuccess  = status === 'success'
  const resolvedLogo = logoUrl ?? '/assets/branding/logo.png'

  return (
    <Overlay>
      <Card>
        {isError ? (
          <ErrorIconWrap>
            <ErrorIconInner>
              <BrokenLinkSvg />
            </ErrorIconInner>
          </ErrorIconWrap>
        ) : (
          <LogoWrap pulse={isSuccess}>
            <BrandLogo src={resolvedLogo} alt={t('ssoConnectingScreen.brandLogo')} />
          </LogoWrap>
        )}

        <Title>
          {isError   && 'We couldn\'t connect'}
          {isSuccess && 'You\'re all set'}
          {isRetrying && 'Still Connecting…'}
          {status === 'connecting' && 'Connecting Your Account'}
        </Title>

        <Subtitle>
          {isError   && 'Please try again. Your account is safe.'}
          {isSuccess && 'Opening your feed…'}
          {isRetrying && 'We hit a small delay and are trying again.'}
          {status === 'connecting' && 'Usually takes a few seconds to connect and prepare your feed.'}
        </Subtitle>

        {isRetrying && (
          <RetryLabel>{t('ssoConnectingScreen.retryingAttempt', { attempt, max: MAX_ATTEMPTS })}</RetryLabel>
        )}

        {(status === 'connecting' || status === 'retrying') && (
          <DotsRow>
            {Array.from({ length: DOTS_COUNT }).map((_, i) => {
              const dist = Math.min(
                Math.abs(i - activeDot),
                DOTS_COUNT - Math.abs(i - activeDot)
              )
              const level = dist === 0 ? 'center' : dist === 1 ? 'side' : 'off'
              return <Dot key={i} level={level} />
            })}
          </DotsRow>
        )}

        {isError && (
          <>
            <RetryButton onClick={onRetry}>{t('ssoConnectingScreen.tryAgain')}</RetryButton>
            {/* Contact Support — commented out for now; may restore if needed
            <SupportLink
              as="button"
              onClick={onContactSupport}
            >
              Contact Support
              <HeadsetIcon viewBox="0 0 24 24" fill="none">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </HeadsetIcon>
            </SupportLink>
            */}
          </>
        )}
      </Card>
    </Overlay>
  )
}

// ─── Inline SVG for broken-link / no-connection icon ────────────────────────
const BrokenLinkSvg: FC = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 17H7A5 5 0 0 1 7 7h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 7h2a5 5 0 0 1 4 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="12" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

// ─── Animations ──────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.12); }
`

const dotGlow = keyframes`
  0%   { opacity: 0.5; transform: scale(1); }
  50%  { opacity: 1;   transform: scale(1.35); }
  100% { opacity: 0.5; transform: scale(1); }
`

// ─── Styled components ───────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  max-width: 600px;
  margin: 0 auto;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 15vh;
  background-color: #141414;
  background-image: url('/assets/images/welcome-background.svg?v=2');
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  z-index: 9999;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  padding: 0 32px;
  max-width: 340px;
  width: 100%;
  animation: ${fadeIn} 0.3s ease;
`

const LogoWrap = styled.div<{ pulse?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  ${({ pulse: p }) =>
    p &&
    css`animation: ${pulse} 1s ease-in-out infinite;`}
`

const BrandLogo = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  border-radius: 16px;
`

const ErrorIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 161, 255, 0.15);
  border: 2px solid rgba(0, 161, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`

const ErrorIconInner = styled.div`
  color: #00A1FF;
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary, #FAFAFA);
  text-align: center;
  margin: 0 0 8px;
  line-height: 1.3;
`

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: var(--text-quaternary, #A1A1AA);
  text-align: center;
  margin: 0;
  line-height: 1.5;
`

const RetryLabel = styled.span`
  font-size: 12px;
  color: var(--text-quaternary, #A1A1AA);
  margin-top: 6px;
`

const DotsRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 24px;
`

const DOT_BG: Record<string, string> = {
  center: '#00A1FF',
  side: 'rgba(0, 161, 255, 0.5)',
  off: 'rgba(0, 161, 255, 0.15)',
}
const DOT_SHADOW: Record<string, string> = {
  center: '0 0 6px 2px rgba(0, 161, 255, 0.5)',
  side: '0 0 4px 1px rgba(0, 161, 255, 0.3)',
  off: 'none',
}

const Dot = styled.span<{ level: 'center' | 'side' | 'off' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  background-color: ${({ level }) => DOT_BG[level]};
  box-shadow: ${({ level }) => DOT_SHADOW[level]};
  ${({ level }) =>
    level === 'center' &&
    css`animation: ${dotGlow} 0.6s ease-in-out infinite;`}
`

const RetryButton = styled.button`
  margin-top: 28px;
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 12px;
  background: #00A1FF;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover  { opacity: 0.9; }
  &:active { opacity: 0.8; }
`

const SupportLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #00A1FF;
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: 14px;

  &:hover { text-decoration: underline; }
`

const HeadsetIcon = styled.svg`
  width: 16px;
  height: 16px;
  color: #00A1FF;
  flex-shrink: 0;
`

export default SsoConnectingScreen
